import LiveDrawMediaPlayer from "../components/PresentationVideo";
import { connectDraw } from "../utils/drawConnection";
import WinnerSequence from "../components/WinnerSequence";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Card, CircularProgress, Stack, Typography } from "@mui/material";
import FlipCountdown from "../components/FlipCountdown";
import { DrawService } from "../services/draw/DrawService";
import { API } from "../constant/apiConstant";
import { Colors } from "../utils/colors";
import { getAnalyticsIdentity } from "../utils/analyticsIdentity";

const STATE = { SCHEDULED: "SCHEDULED", PRESENTER_CONNECTED: "PRESENTER_CONNECTED", ANNOUNCEMENT: "ANNOUNCEMENT", READY_TO_START: "READY_TO_START", LIVE_DRAW: "LIVE_DRAW", CELEBRATION: "CELEBRATION", RESULTS: "RESULTS", FINALIZATION_ERROR: "FINALIZATION_ERROR" };


export default function LiveDraw() {
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [transportDegraded,setTransportDegraded]=useState(false);
  const lastSnapshot=useRef(0);
  const [playbackSyncVersion, setPlaybackSyncVersion] = useState(0);
  const [resultReplayKey, setResultReplayKey] = useState(0);
  const [canReplayResults, setCanReplayResults] = useState(false);
  const [celebrationFailed, setCelebrationFailed] = useState(false);
  const [celebrationPlaybackBlocked, setCelebrationPlaybackBlocked] = useState(false);
  const [resultsAudioBlocked, setResultsAudioBlocked] = useState(false);
  const [announcementClock, setAnnouncementClock] = useState(Date.now());
  const videoRef = useRef(null);
  const celebrationVideoRef = useRef(null);
  const resultsAudioRef = useRef(null);
  const serverOffsetRef = useRef(0);
  const refreshInFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    try {
      const current = await DrawService.getLiveDisplayDraw({
        includeResults: true
      });
      if (current?.server_time) {
        const time=Date.parse(current.server_time);
        if(!Number.isFinite(time)||time<lastSnapshot.current)return;
        lastSnapshot.current=time;serverOffsetRef.current=time-Date.now();
      }
      setDraw((previous) => {
        const sameDraw = Number(previous?.draw_id) === Number(current?.draw_id);
        return {
          ...current,
          winners: Array.isArray(current?.winners)
            ? sameDraw && JSON.stringify(previous?.winners)===JSON.stringify(current.winners) ? previous.winners : current.winners
            : sameDraw
              ? previous?.winners || []
              : []
        };
      });
      setError("");
    } catch (_error) {
      if ([401,403,404].includes(_error?.status)) setDraw(null);
      setError("Draw state could not be refreshed. Retrying automatically; displayed information may be delayed.");
      throw _error;
    } finally {
      refreshInFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => connectDraw({
    url: `${API.BASE_URL}/api/draws/live-events`, refresh,
    onReconnect:()=>setPlaybackSyncVersion(value=>value+1),
    onStatus:status=>{if(status.events!==undefined)setTransportDegraded(!status.events);}
  }), [refresh]);

  useEffect(() => {
    if (!draw?.draw_id) return undefined;
    const { visitorId, sessionId } = getAnalyticsIdentity();
    const heartbeat = () => fetch(`${API.BASE_URL}/api/analytics/draw-viewer`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ draw_id: draw.draw_id, visitor_id: visitorId, session_id: sessionId })
    }).catch(() => null);
    heartbeat(); const timer = window.setInterval(heartbeat, 20000);
    return () => window.clearInterval(timer);
  }, [draw?.draw_id]);

  useEffect(() => {
    const boundary = draw?.state === STATE.ANNOUNCEMENT
      ? draw.announcement_ends_at
      : draw?.state === STATE.LIVE_DRAW
      ? draw.presentation_ends_at
      : draw?.state === STATE.CELEBRATION
      ? draw.celebration_ends_at
      : draw?.state === STATE.RESULTS
        ? draw.result_display_until
        : null;
    if (!boundary) return undefined;
    const remaining = new Date(boundary).getTime() - (Date.now() + serverOffsetRef.current);
    if (remaining <= 0) {
      refresh().catch(()=>{});
      return undefined;
    }
    const timer = window.setTimeout(()=>refresh().catch(()=>{}), Math.min(remaining + 100, 2147483647));
    return () => window.clearTimeout(timer);
  }, [draw?.presentation_ends_at, draw?.announcement_ends_at, draw?.celebration_ends_at, draw?.result_display_until, draw?.state, refresh]);

  useEffect(() => {
    if (draw?.state !== STATE.ANNOUNCEMENT) return undefined;
    const tick = () => setAnnouncementClock(Date.now() + serverOffsetRef.current);
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [draw?.state]);

  useEffect(() => {
    if (draw?.state !== STATE.CELEBRATION) {
      setCelebrationFailed(false);
      setCelebrationPlaybackBlocked(false);
    }
  }, [draw?.draw_id, draw?.state]);

  const showResults = draw?.state === STATE.RESULTS;

  useEffect(() => {
    setResultReplayKey(0);
    const duration = Number(draw?.results_animation_duration_seconds || Math.max(18, Number(draw?.winners?.length || 1) * 5));
    setCanReplayResults(Date.now() + serverOffsetRef.current >= Date.parse(draw?.results_started_at) + duration * 1000);
  }, [draw?.draw_id, draw?.results_started_at, draw?.results_animation_duration_seconds, draw?.winners?.length]);

  useEffect(() => {
    const video = celebrationVideoRef.current;
    if (draw?.state !== STATE.CELEBRATION || !video || !draw.celebration_video_url) return;
    video.muted = true;
    const playback = video.play();
    if (playback?.then) {
      playback.then(() => setCelebrationPlaybackBlocked(false)).catch(() => setCelebrationPlaybackBlocked(true));
    }
  }, [draw?.celebration_video_url, draw?.state]);

  useEffect(() => {
    if (draw?.state !== STATE.LIVE_DRAW || !(draw.presentation_started_at || draw.started_at) || !videoRef.current) return;
    const player = videoRef.current;
    const syncPlayback = () => {
      const elapsed = Math.max(0, (Date.now() + serverOffsetRef.current - new Date(draw.presentation_started_at || draw.started_at).getTime()) / 1000);
      if (Number.isFinite(player.duration)) {
        const target = player.duration > 0 ? elapsed % player.duration : 0;
        if (Math.abs(player.currentTime - target) > 3) player.currentTime = target;
      }
      player.play().catch(() => {});
    };
    player.addEventListener("loadedmetadata", syncPlayback, { once: true });
    if (player.readyState >= 1) syncPlayback();
    return () => player.removeEventListener("loadedmetadata", syncPlayback);
  }, [draw?.state, draw?.started_at, draw?.presentation_started_at, draw?.draw_video_url, playbackSyncVersion]);

  useEffect(() => {
    const audio = resultsAudioRef.current;
    if (!showResults || !audio || !draw.results_audio_url) {
      if (audio) audio.pause();
      return undefined;
    }
    audio.loop = true;
    audio.play().then(() => setResultsAudioBlocked(false)).catch(() => setResultsAudioBlocked(true));
    return () => audio.pause();
  }, [draw?.results_audio_url, showResults]);

  const videoUrl = draw?.state === STATE.PRESENTER_CONNECTED ? draw.pre_draw_video_url : draw?.draw_video_url;
  const scheduledAt = draw?.scheduled_at ? new Date(draw.scheduled_at) : null;
  const winnerCreditDuration = Number(draw?.results_animation_duration_seconds || Math.max(18, Number(draw?.winners?.length || 1) * 5));
  const immersiveStage = [STATE.PRESENTER_CONNECTED, STATE.ANNOUNCEMENT, STATE.LIVE_DRAW, STATE.CELEBRATION].includes(draw?.state) || showResults;
  const showPageHeading = ![STATE.READY_TO_START, STATE.ANNOUNCEMENT, STATE.LIVE_DRAW, STATE.CELEBRATION].includes(draw?.state) && !showResults;

  return (
    <Box sx={{ bgcolor: Colors.secondary, display: "flex", justifyContent: "center", p: { xs: immersiveStage ? 0.5 : 1.5, md: 4 }, minHeight: "70vh", pb: "calc(100px + env(safe-area-inset-bottom))" }}>
      <Card sx={{ width: "100%", maxWidth: 960, p: { xs: immersiveStage ? 1 : 2, sm: 4 }, textAlign: "center", bgcolor: "#071f36", color: "#fff", overflow: "hidden" }}>
        {showPageHeading && (
          <Typography variant="h4" fontWeight={800} color="#ffb300" mb={3}>Live Draw</Typography>
        )}
        {loading && <Typography>Loading live draw…</Typography>}
        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
        {!error && transportDegraded && <Alert severity="info">Live event connection interrupted. Current draw state is checked automatically.</Alert>}
        {!loading && draw && (
          <Stack spacing={immersiveStage ? { xs: 1.5, sm: 3 } : 3} alignItems="center">
            {draw.state === STATE.SCHEDULED && <Typography variant="h5" fontWeight={800}>NEXT LIVE DRAW</Typography>}
            {draw.state === STATE.FINALIZATION_ERROR && <Alert severity="error">Results require administrator review. The draw will not be rerun automatically.</Alert>}
            {showResults && <Typography variant="h4" fontWeight={900} color="#ffcc33">WINNERS</Typography>}

            {draw.state === STATE.SCHEDULED && draw.scheduled_at && (
              <Stack spacing={1.25} alignItems="center">
                <Typography fontWeight={700}>Draw: {draw.draw_code}</Typography>
                <Typography>
                  Date: {scheduledAt.toLocaleDateString("en-NG", { year: "numeric", month: "long", day: "numeric" })}
                </Typography>
                <Typography>
                  Time: {scheduledAt.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit", hour12: true })}
                </Typography>
                <Box sx={{ pt: 1 }}><FlipCountdown targetDate={scheduledAt} /></Box>
              </Stack>
            )}

            {draw.state === STATE.ANNOUNCEMENT && (() => {
              const startedAt = new Date(draw.announcement_started_at || announcementClock).getTime();
              const totalSeconds = Number(draw.announcement_duration_seconds || 60);
              const activeAnnouncements = (draw.announcements || []).filter((copy) => String(copy || "").trim());
              const pageCount = Math.max(activeAnnouncements.length, 1);
              const pageIndex = Math.min(pageCount - 1, Math.floor(Math.max(0, announcementClock - startedAt) / ((totalSeconds * 1000) / pageCount)));
              return (
                <Card sx={{ width: "100%", maxWidth: 540, height: { xs: "78svh", sm: 680 }, bgcolor: "#071f36", color: "#fff", p: { xs: 3, sm: 5 }, display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
                  <Stack spacing={2} alignItems="stretch" sx={{ width: "100%", pt: { xs: 2, sm: 3 } }}>
                    <Typography variant="h4" fontWeight={900} color="#ffcc33">ANNOUNCEMENT</Typography>
                    <Box sx={{ pt: { xs: 1.5, sm: 2 } }}>
                      <Typography sx={{ whiteSpace: "pre-line", textAlign: "left", overflowWrap: "break-word", fontSize: { xs: "1.25rem", sm: "1.65rem" }, lineHeight: 1.45, fontWeight: 700 }}>{activeAnnouncements[pageIndex] || "Live draw announcement"}</Typography>
                    </Box>
                    <Typography color="rgba(255,255,255,.6)" textAlign="center">{pageIndex + 1} / {activeAnnouncements.length}</Typography>
                  </Stack>
                </Card>
              );
            })()}

            {draw.state === STATE.READY_TO_START && (
              <Stack spacing={2} alignItems="center">
                <Typography variant="h4" fontWeight={900}>DRAW {draw.draw_code}</Typography>
                <Typography>{scheduledAt?.toLocaleDateString("en-NG", { year: "numeric", month: "long", day: "numeric" })}</Typography>
                <Typography>{scheduledAt?.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit", hour12: true })}</Typography>
                <Stack spacing={0.75} sx={{ py: 1 }} alignItems="center">
                  <Typography fontWeight={800}>
                    WINNERS: {Number(draw.display_winner_count || 0).toLocaleString("en-NG")}
                  </Typography>
                  <Typography fontWeight={800} color="#ffcc33">
                    PRIZE PER WINNER: ₦{Number(draw.winner_prize_amount || 0).toLocaleString("en-NG")}
                  </Typography>
                </Stack>
                <Button variant="contained" disabled sx={{ minWidth: 260, py: 1.5, fontWeight: 900 }}>LIVE DRAW STARTS SOON</Button>
              </Stack>
            )}

            {[STATE.PRESENTER_CONNECTED, STATE.LIVE_DRAW].includes(draw.state) && (videoUrl ? (
              <LiveDrawMediaPlayer
                ref={draw.state === STATE.LIVE_DRAW ? videoRef : null}
                src={videoUrl}
                loop
                startedAt={draw.state===STATE.PRESENTER_CONNECTED?draw.presenter_connected_at:draw.presentation_started_at}
                serverOffset={serverOffsetRef.current}
              />
            ) : <Alert severity="info">{draw.state === STATE.LIVE ? "LIVE DRAW IN PROGRESS" : "The draw starts soon. Keep this page open."}</Alert>)}

            {draw.state === STATE.CELEBRATION && !celebrationFailed && (draw.celebration_video_url ? (
              <Box sx={{ position: "relative", width: "100%", display: "flex", justifyContent: "center" }}>
              <LiveDrawMediaPlayer ref={celebrationVideoRef} src={draw.celebration_video_url}
                onAutoplayBlocked={() => setCelebrationPlaybackBlocked(true)}
                onPlaying={() => setCelebrationPlaybackBlocked(false)}
                onEnded={() => setCelebrationFailed(true)}
                onError={() => setCelebrationFailed(true)} />
              {celebrationPlaybackBlocked ? (
                <Button
                  variant="contained"
                  onClick={() => celebrationVideoRef.current?.play().then(() => setCelebrationPlaybackBlocked(false)).catch(() => {})}
                  sx={{ position: "absolute", inset: "50% auto auto 50%", transform: "translate(-50%, -50%)", bgcolor: "rgba(0,0,0,.75)", color: "#fff" }}
                >
                  Tap to Play
                </Button>
              ) : null}
              </Box>
            ) : (
              <Stack spacing={2} alignItems="center" sx={{ py: 8 }}>
                <CircularProgress sx={{ color: "#ffcc33" }} />
                <Typography>Preparing the winner cast…</Typography>
              </Stack>
            ))}
            {draw.state === STATE.CELEBRATION && celebrationFailed ? (
              <Stack spacing={2} alignItems="center" sx={{ py: 8 }}>
                <CircularProgress sx={{ color: "#ffcc33" }} />
                <Typography>Preparing the winner cast…</Typography>
              </Stack>
            ) : null}

            {showResults && (
              <>
              {draw.results_audio_url ? <audio ref={resultsAudioRef} src={draw.results_audio_url} preload="auto" loop /> : null}
              {resultsAudioBlocked ? (
                <Button variant="outlined" onClick={() => resultsAudioRef.current?.play().then(() => setResultsAudioBlocked(false)).catch(() => {})}
                  size="small"
                  sx={{ alignSelf: "flex-end", color: "#fff", borderColor: "rgba(255,255,255,.45)", py: 0.25, px: 1 }}>🔊 Play Sound</Button>
              ) : null}
              <WinnerSequence count={draw.winners?.length || 0} startedAt={draw.results_started_at}
                durationSeconds={winnerCreditDuration} serverOffset={serverOffsetRef.current}
                replayKey={resultReplayKey} onComplete={() => setCanReplayResults(true)}
                sx={{ bgcolor: "#020b13", borderRadius: 2, border: "1px solid rgba(255,204,51,.2)", px: { xs: 2, sm: 4 } }}>
                {draw.winners?.length ? (
                  <Stack
                    spacing={{ xs: 2, sm: 3 }}
                    sx={{
                      width: "100%",
                      py: { xs: 2, sm: 3 },

                    }}
                  >
                    {(draw.winners || []).map((winner, index) => (
                      <Box
                        key={winner.ticket_code}
                        data-winner-index={index}
                        sx={{
                          position: "relative",
                          width: "100%",
                          minWidth: 0,
                          px: { xs: 5, sm: 7 },
                          boxSizing: "border-box"
                        }}
                      >
                        <Typography
                          sx={{
                            position: "absolute",
                            left: { xs: 4, sm: 12 },
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: { xs: "1.15rem", sm: "1.35rem" },
                            lineHeight: 1.15,
                            fontWeight: 800,
                            color: "#ffcc33"
                          }}
                        >
                          {index + 1}.
                        </Typography>

                        <Stack spacing={0.25} alignItems="center" textAlign="center" sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: { xs: "1.15rem", sm: "1.35rem" },
                              lineHeight: 1.15,
                              fontWeight: 800,
                              overflowWrap: "anywhere"
                            }}
                          >
                            {String(winner.first_name || "Winner").toUpperCase()}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#d7e3ef",
                              letterSpacing: { xs: 1, sm: 2 },
                              overflowWrap: "anywhere"
                            }}
                          >
                            {winner.ticket_code}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: { xs: "1.05rem", sm: "1.2rem" },
                              lineHeight: 1.2,
                              fontWeight: 800
                            }}
                            color="#ffcc33"
                          >
                            ₦{Number(
                              winner.gross_winning_amount ?? winner.winning_amount ?? 0
                            ).toLocaleString("en-NG")}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : <Typography mt={4}>Official results are being prepared.</Typography>}
              </WinnerSequence>
              </>
            )}
            {showResults && draw.winners?.length ? (
              <Button
                variant="contained"
                disabled={!canReplayResults}
                onClick={() => {
                  setCanReplayResults(false);
                  setResultReplayKey((value) => value + 1);
                  const audio = resultsAudioRef.current;
                  if (audio) {
                    audio.currentTime = 0;
                    audio.play().then(() => setResultsAudioBlocked(false)).catch(() => setResultsAudioBlocked(true));
                  }
                }}
                sx={{ bgcolor: "#ffcc33", color: "#071f36", fontWeight: 800 }}
              >
                Replay Results
              </Button>
            ) : null}
          </Stack>
        )}
      </Card>
    </Box>
  );
}
